import { formatUuidT } from "../../../flatbuffers/display";

/**
 * The server and organization a bank belongs to, recovered from the bank alone.
 *
 * The standalone bank and lift editors (`/edge-management/bank/edit`,
 * `/edge-management/lift/edit`) are opened by URL with nothing but `?idBank=`,
 * inside a fresh `WizardProvider` that knows no server. Every list they load
 * (channels, adapters, the bank picker) is keyed by server, so this is the
 * first thing each of them has to work out.
 *
 * `Archive.Bank` — what `GET /banks/:id` returns on the FlatBuffer path — has a
 * `channel_id` and an `org_id` and no server id, nested or otherwise; the
 * server is a property of the bank's channel. The server-id fields read first
 * below are what the legacy JSON response carried, and are kept so that shape
 * still resolves without a second request.
 */
export interface StandaloneBankScope {
  serverId: string;
  organizationId: string;
}

/** Reads one channel by id; resolves to the channel record, or null/undefined. */
export type FetchChannel = (channelId: string) => Promise<unknown>;

// A bank or channel arrives as either the FlatBuffer Object API type or the
// legacy JSON shape, and this reads both, hence the loose parameter types.
function serverIdOnBank(bank: any): string {
  return (
    formatUuidT(bank?.serverId) ||
    bank?.server_id ||
    bank?.channel?.server_id ||
    formatUuidT(bank?.channel?.server?.id) ||
    bank?.channel?.server?.id ||
    ""
  );
}

function organizationIdOnBank(bank: any): string {
  return (
    formatUuidT(bank?.orgId) ||
    bank?.organization_id ||
    formatUuidT(bank?.channel?.server?.org?.id) ||
    bank?.channel?.server?.organization_id ||
    bank?.channel?.server?.org?.id ||
    ""
  );
}

function channelIdOnBank(bank: any): string {
  return formatUuidT(bank?.channelId) || bank?.channel_id || "";
}

export async function resolveStandaloneBankScope(
  bank: any,
  fetchChannel: FetchChannel,
): Promise<StandaloneBankScope> {
  let serverId = serverIdOnBank(bank);
  let organizationId = organizationIdOnBank(bank);
  const channelId = channelIdOnBank(bank);
  if (!serverId && channelId) {
    try {
      const raw: any = await fetchChannel(channelId);
      const channel = raw?.data ?? raw;
      serverId = formatUuidT(channel?.serverId) || channel?.server_id || "";
      organizationId =
        organizationId || formatUuidT(channel?.organizationId) || channel?.organization_id || "";
    } catch {
      // Left unresolved: the caller shows the editor without a server rather
      // than crashing on it.
    }
  }
  return { serverId, organizationId };
}
